import { Component, EventEmitter, OnDestroy, OnInit, ViewEncapsulation, Output, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

import { DocumentMillDropdownFilter, DynamicDatacenterFilter } from '../../../../models/datacenter-filters';
import { pageTransitions } from '../../../../animations/page-transitions';
import { DatacenterFilterApiService } from '../../../../services/datacenter-filter-api.service';
import { DatacenterType } from '../../../../models/datacenter-aggrid-view';
import { LoadingSpinnerComponent } from '../../../../components/utils/loading-spinner.component';

interface ItemValue {
	code?: string | undefined;
	translation?: string;
	entityName?: string;
}

@Component({
	selector: 'copo-document-mill-dropdown-filter',
	templateUrl: './document-mill-dropdown-filter.component.html',
	styleUrl: './document-mill-dropdown-filter.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	providers: [DatacenterFilterApiService],
	imports: [
		NgIf,
		NgFor,
		MatIconModule,
		MatRippleModule,
		LoadingSpinnerComponent,

		MatFormFieldModule,

		MatSelectModule,

		MatOptionModule,

		MatButtonModule,
	]
})
export class DocumentMillDropdownFilterComponent
implements OnInit, OnDestroy, DynamicDatacenterFilter<DocumentMillDropdownFilter> {
	@Input() filter?: DocumentMillDropdownFilter;
	@Output() readonly removeFilter = new EventEmitter<void>();
	@Input() isDefaultFilter?: boolean;
	filteredItems?: ItemValue[];
	isLoading = true;
	showTransportModeDropdown = false;
	datacenterType: DatacenterType;
	itemsSub?: Subscription;

	constructor(
		private readonly filterApiService: DatacenterFilterApiService,
		private readonly route: ActivatedRoute
	) {
		this.datacenterType = <DatacenterType> this.route.snapshot.queryParams['type'];
	}

	ngOnInit() {
		const values = this.filter?.getValues();
		if (values && values.length > 0) {
			this.filteredItems = values;
		}
		this.itemsSub = this.filterApiService.sendDocumentDropdownRequest(this.filter).subscribe({
			next: value => {
				this.filteredItems = [
					...value
						.map(item => {
							// If item.code is an array, take only the first code
							if (Array.isArray(item.code)) {
								return {
									...item,
									code: item.code.join(' || ')
								};
							}
							return item;
						})
						.sort((f1, f2) => f1.translation.localeCompare(f2.translation))
				];
				this.isLoading = false;
			},
			error: (error: unknown) => {
				console.error(error);
				this.isLoading = false;
			}
		});
	}

	ngOnDestroy() {
		if (this.itemsSub) {
			this.itemsSub.unsubscribe();
		}
	}

	removeValue(index: number) {
		if (!this.filter) {
			return;
		}
		this.filter.getValues().splice(index, 1);
		if (!this.filter.valueSize()) {
			this.removeFilter.emit();
		}
	}

	trackByFunc(index: number) {
		return index;
	}

	getPreparedFilterValues() {
		if (this.filter) {
			return {
				id: this.filter.filterId,
				values: this.filter.getValues()
					.filter(filterValue => !filterValue.translation || !filterValue.code)
			};
		}
		return undefined;
	}

	selectionChanged(
		item: ItemValue,
		selectionResult: string
	) {
		const selection = this.filteredItems?.find(fItem => fItem.code === selectionResult);
		if (!selection) {
			return;
		}
		Object.assign(item, selection);
	}
}
